import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BehaviorScore, DailyHabitScore, FundamentalsResponse } from '@/api/types';
import { scoreColor, fmtScore, FUND_DEFAULT_COLORS } from '@/utils/analytics';

interface FundamentalsTabProps {
  apiFundamentals?: FundamentalsResponse;
  /** Estado de carga del fetch de fundamentales — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isLoadingFundamentals?: boolean;
  /** Estado de error del fetch de fundamentales — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isErrorFundamentals?: boolean;
}

const DIST_COLORS = ['var(--cp-danger-light)', '#f59e0b', '#10b981', 'var(--cp-success)'];

/** Normaliza el conteo por nivel a fracciones 0-1 de largo 4 (índice 0 = nivel 1). */
function normalizeDistribution(distribution: number[] | undefined): number[] {
  const counts = [0, 1, 2, 3].map((level) => distribution?.[level] ?? 0);
  const total = counts.reduce((sum, count) => sum + count, 0);
  return counts.map((count) => (total > 0 ? count / total : 0));
}

interface DistributionBarsProps {
  distribution: number[] | undefined;
}

/** Reparto de opiniones por nivel 1–4. Compartido por comportamientos y hábitos. */
function DistributionBars({ distribution }: DistributionBarsProps) {
  const fractions = normalizeDistribution(distribution);

  return (
    <div className="au-beh-dist">
      {fractions.map((fraction, level) => (
        <div key={level} className="au-dist-bar">
          <span className="au-dist-level">{level + 1}</span>
          <div className="au-dist-track">
            <div
              className="au-dist-fill"
              style={{ width: `${Math.round(fraction * 100)}%`, background: DIST_COLORS[level] }}
            />
          </div>
          <span className="au-dist-pct">{Math.round(fraction * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

interface HabitListProps {
  dailyHabits: DailyHabitScore[];
  evalLabel: (count: number) => string;
}

/** Daily habits de un comportamiento o de un fundamental sin comportamientos. */
function HabitList({ dailyHabits, evalLabel }: HabitListProps) {
  if (dailyHabits.length === 0) return null;

  return (
    <ul className="au-habit-list">
      {dailyHabits.map((habit) => (
        <li key={habit.id} className="au-habit-item">
          <span className="au-habit-name">{habit.name}</span>
          <span
            className="au-habit-score"
            style={{ color: habit.score > 0 ? scoreColor(habit.score) : undefined }}
          >
            {habit.score > 0 ? fmtScore(habit.score) : '—'}
          </span>
          <span className="au-habit-evals">{evalLabel(habit.evaluations)}</span>
        </li>
      ))}
    </ul>
  );
}

interface BehaviorRowProps {
  behavior: BehaviorScore;
  color: string;
  isExpanded: boolean;
  onToggle: () => void;
  evalLabel: (count: number) => string;
  noObsLabel: (count: number) => string;
}

/**
 * Un comportamiento (Actitud y Comportamiento) dentro de su fundamental. Es la unidad de
 * lectura del reporte: el promedio del fundamental mezcla conductas distintas y no dice qué
 * corregir, así que cuando hay comportamientos configurados son ellos los que llevan el score,
 * la distribución y el conteo de evaluaciones.
 */
function BehaviorRow({
  behavior, color, isExpanded, onToggle, evalLabel, noObsLabel,
}: BehaviorRowProps) {
  return (
    <div className={`au-beh-block${isExpanded ? ' expanded' : ''}`}>
      <button
        type="button"
        className="au-beh-head"
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <span className="au-beh-color" style={{ background: color }} />
        <span className="au-beh-name">{behavior.name}</span>
        <span className="au-beh-bar">
          <span
            className="au-beh-bar-fill"
            style={{ width: `${(behavior.score / 4) * 100}%`, background: color }}
          />
        </span>
        <span
          className="au-beh-score"
          style={{ color: behavior.score > 0 ? scoreColor(behavior.score) : undefined }}
        >
          {behavior.score > 0 ? fmtScore(behavior.score) : '—'}
        </span>
        <span className="au-beh-evals">{evalLabel(behavior.evaluations)}</span>
        <i className="fas fa-chevron-down au-beh-arrow" aria-hidden="true" />
      </button>

      {isExpanded && (
        <div className="au-beh-detail">
          <div className="au-beh-noobs">{noObsLabel(behavior.noObs)}</div>
          <DistributionBars distribution={behavior.distribution} />
          <HabitList dailyHabits={behavior.dailyHabits} evalLabel={evalLabel} />
        </div>
      )}
    </div>
  );
}

export function FundamentalsTab({
  apiFundamentals,
  isLoadingFundamentals = false,
  isErrorFundamentals = false,
}: FundamentalsTabProps) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const evalLabel = (count: number) => t('analytics.summary.evaluationsCount', { count });
  const noObsLabel = (count: number) => t('analytics.fundamentals.noObsCount', { count });

  const fundamentals = useMemo(
    () => (apiFundamentals ?? []).map((fundamental, index) => ({
      ...fundamental,
      color: fundamental.color ?? FUND_DEFAULT_COLORS[index % FUND_DEFAULT_COLORS.length]!,
    })),
    [apiFundamentals],
  );

  const stateMessage = useMemo(() => {
    if (isLoadingFundamentals) return t('common.loading');
    if (isErrorFundamentals) return t('common.error');
    if (fundamentals.length === 0) return t('analytics.noData');
    return null;
  }, [isLoadingFundamentals, isErrorFundamentals, fundamentals, t]);

  function toggle(dimensionId: string): void {
    setExpandedId((current) => (current === dimensionId ? null : dimensionId));
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-layer-group" aria-hidden="true" />
            {t('analytics.fundamentals.title')}
          </div>
          <div className="au-card-sub">{t('analytics.fundamentals.behaviorSubtitle')}</div>
        </div>
      </div>

      {stateMessage && (
        <div className={`au-fund-state${isErrorFundamentals ? ' au-fund-state-error' : ''}`}>
          {isLoadingFundamentals && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
          {isErrorFundamentals && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
          {' '}{stateMessage}
        </div>
      )}

      {!stateMessage && (
        <div className="au-fund-list">
          {fundamentals.map((fundamental) => {
            const hasBehaviors = fundamental.behaviors.length > 0;
            const isExpanded = expandedId === fundamental.id;

            return (
              <section key={fundamental.id} className="au-fund-section">
                <header className="au-fund-section-head">
                  <span className="au-fund-color" style={{ background: fundamental.color }} />
                  <h3 className="au-fund-section-title">{fundamental.name}</h3>
                  <span
                    className="au-fund-section-score"
                    style={{ color: fundamental.score > 0 ? scoreColor(fundamental.score) : undefined }}
                  >
                    {fundamental.score > 0 ? fmtScore(fundamental.score) : '—'}
                  </span>
                  <span className="au-fund-section-evals">{evalLabel(fundamental.evaluations)}</span>
                </header>

                {hasBehaviors ? (
                  <div className="au-beh-group">
                    {fundamental.behaviors.map((behavior) => (
                      <BehaviorRow
                        key={behavior.id}
                        behavior={behavior}
                        color={fundamental.color}
                        isExpanded={expandedId === behavior.id}
                        onToggle={() => toggle(behavior.id)}
                        evalLabel={evalLabel}
                        noObsLabel={noObsLabel}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={`au-beh-block${isExpanded ? ' expanded' : ''}`}>
                    <button
                      type="button"
                      className="au-beh-head"
                      aria-expanded={isExpanded}
                      onClick={() => toggle(fundamental.id)}
                    >
                      <span className="au-beh-name">{t('analytics.fundamentals.dailyHabits')}</span>
                      <span className="au-beh-evals">{noObsLabel(fundamental.noObs)}</span>
                      <i className="fas fa-chevron-down au-beh-arrow" aria-hidden="true" />
                    </button>
                    {isExpanded && (
                      <div className="au-beh-detail">
                        <DistributionBars distribution={fundamental.distribution} />
                        <HabitList dailyHabits={fundamental.dailyHabits} evalLabel={evalLabel} />
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
